import { ref } from 'vue'
import axios from 'axios'

// Global state for currency symbol
const currencySymbol = ref('€')
const isLoaded = ref(false)

/**
 * Composable for currency formatting with dynamic symbol
 */
export function useCurrency() {
    /**
     * Load currency symbol from backend
     */
    const loadCurrencySymbol = async () => {
        if (isLoaded.value) return

        try {
            const response = await axios.get('/api/general-settings')
            if (response.data.success && response.data.data.currency_symbol) {
                currencySymbol.value = response.data.data.currency_symbol
            }
            isLoaded.value = true
        } catch (error) {
            // Silence 401 errors as they are expected when user is not logged in
            if (error.response && error.response.status === 401) {
                currencySymbol.value = '€'
                return
            }
            // console.warn('Failed to load currency symbol, using default:', error)
            // Do not set isLoaded = true on error, so we can retry later (e.g. after login)
            currencySymbol.value = '€'
        }
    }

    /**
     * Format amount with currency symbol
     * @param {number} amount - The amount to format
     * @param {boolean} showSymbol - Whether to show the currency symbol
     * @returns {string} Formatted currency string
     */
    const formatCurrency = (amount, showSymbol = true, decimals = 2) => {
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(amount || 0)

        return showSymbol ? `${formatted} ${currencySymbol.value}` : formatted
    }

    /**
     * Get the current currency symbol
     * @returns {string} Current currency symbol
     */
    const getCurrencySymbol = () => {
        return currencySymbol.value
    }

    /**
     * Set currency symbol (useful after updating settings)
     * @param {string} symbol - New currency symbol
     */
    const setCurrencySymbol = (symbol) => {
        currencySymbol.value = symbol
    }

    /**
     * Format amount with compact notation (K, M, B) for large numbers
     * @param {number} amount - The amount to format
     * @returns {string} Formatted compact currency string
     */
    const formatCompactCurrency = (amount) => {
        const absAmount = Math.abs(amount || 0)

        if (absAmount >= 1000000) {
            // Millions
            const formatted = (amount / 1000000).toFixed(1)
            return `${formatted}M ${currencySymbol.value}`
        } else if (absAmount >= 1000) {
            // Thousands
            const formatted = (amount / 1000).toFixed(1)
            return `${formatted}K ${currencySymbol.value}`
        } else {
            // Less than 1000, use regular format without decimals
            const formatted = Math.round(amount || 0)
            return `${formatted} ${currencySymbol.value}`
        }
    }

    /**
     * Format number with US-style separators
     * @param {number} value - The value to format 
     * @param {number} decimals - Precision
     * @returns {string} Formatted string
     */
    const formatDecimal = (value, decimals = 2) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals
        }).format(value || 0)
    }

    /**
     * Parse formatted string back to number
     * @param {string|number} value - The value to parse
     * @returns {number} Clean number
     */
    const parseDecimal = (value) => {
        if (typeof value === 'number') return value
        if (!value) return 0
        // Remove commas and parse
        return parseFloat(String(value).replace(/,/g, '')) || 0
    }

    return {
        currencySymbol,
        loadCurrencySymbol,
        formatCurrency,
        formatDecimal,
        parseDecimal,
        formatCompactCurrency,
        getCurrencySymbol,
        setCurrencySymbol
    }
}
